import { useState } from 'react';
import type { AdminTeam, AdminTeamDetail } from '@boardflight/shared';
import {
  CLAUDECODE_SANDBOX_SIZES,
  DEFAULT_CLAUDECODE_SANDBOX_SIZE,
  DEFAULT_SANDBOX_MINUTES,
  DEFAULT_SANDBOX_SIZE,
  MAX_SANDBOX_MINUTES,
  MIN_SANDBOX_MINUTES,
  SANDBOX_SIZES,
  sandboxSizeSpec,
} from '@boardflight/shared';
import { api, put } from '../api.js';
import { Async, Filter, ListMore, Modal, Panel, Time, useAsync, useDebounced, useListLength, useSelection } from '../ui.js';
import { FeatureList } from './FeatureList.js';

/**
 * Teams: plan, sandbox seats, sizes and session length, roster, and what the plan reaches (§2).
 *
 * The plan is the team's half of what anybody in it has. People pay per team, so a paid feature is
 * the same for everybody in the team; a beta or alpha feature is each person's own, set on Accounts.
 */

/**
 * The filter, in the URL's terms.
 *
 * Every field here is a query parameter the route understands, and the empty string is *no
 * opinion* in all of them — so "clear the filter" is the state the page loads with rather than a
 * third value somewhere that means the same thing.
 */
interface TeamFilter {
  q: string;
  plan: string;
  size: string;
  sort: string;
}

const EMPTY: TeamFilter = { q: '', plan: '', size: '', sort: 'created' };

export function Teams() {
  const [filter, setFilter] = useState<TeamFilter>(EMPTY);
  const [selected, setSelected] = useSelection('teams');

  // The text debounces and the selects do not: a dropdown is one decision and should land at once,
  // where a search box is a decision still being made.
  const q = useDebounced(filter.q);
  const [shown, showMore] = useListLength([q, filter.plan, filter.size, filter.sort].join('\n'));

  const list = useAsync(
    () =>
      api<{ items: AdminTeam[]; limit: number; total: number }>(
        `/v1/admin/teams?q=${encodeURIComponent(q)}&plan=${filter.plan}&size=${filter.size}` +
          `&sort=${filter.sort}&limit=${shown}`,
      ),
    [q, filter.plan, filter.size, filter.sort, shown],
  );

  const set = (patch: Partial<TeamFilter>) => setFilter((current) => ({ ...current, ...patch }));
  const filtered = filter.plan !== '' || filter.size !== '' || filter.q !== '';

  return (
    <>
      <Panel
        title="Teams"
        actions={
          <div className="filters">
            <label className="filter">
              <span className="muted small">Search</span>
              <input
                type="text"
                value={filter.q}
                placeholder="Slug, name or id"
                onChange={(e) => set({ q: e.target.value })}
              />
            </label>
            <Filter
              label="Plan"
              value={filter.plan}
              onChange={(plan) => set({ plan })}
              options={[
                { value: '', label: 'Any plan' },
                { value: 'free', label: 'free' },
                { value: 'paid', label: 'paid' },
              ]}
            />
            {/* The question this one answers is "who is on something other than the default", which
                is the `SELECT … WHERE sandbox_size != 'smallplus'` migration 0093 left the default. */}
            <Filter
              label="Size"
              value={filter.size}
              onChange={(size) => set({ size })}
              options={[
                { value: '', label: 'Any size' },
                ...SANDBOX_SIZES.map((spec) => ({ value: spec.size, label: spec.size })),
              ]}
            />
            <Filter
              label="Sort"
              value={filter.sort}
              onChange={(sort) => set({ sort })}
              options={[
                { value: 'created', label: 'Newest' },
                { value: 'name', label: 'Name' },
                { value: 'members', label: 'Most members' },
                { value: 'games', label: 'Most games' },
                { value: 'seats', label: 'Most seats' },
              ]}
            />
            {filtered ? <button onClick={() => setFilter(EMPTY)}>Clear</button> : null}
          </div>
        }
      >
        <Async state={list} empty="No teams.">
          {(data) =>
            data.items.length === 0 ? (
              // Two different facts, and the table has to say which: nothing matched, versus there
              // are no teams at all. An operator who filtered to `extralarge` and saw a bare "No
              // teams." would reasonably conclude the console was broken.
              <p className="muted">
                {filtered ? 'No team matches this filter.' : 'No teams.'}
              </p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>Plan</th>
                      <th className="num">Seats</th>
                      <th>Size</th>
                      <th className="num">Members</th>
                      <th className="num">Games</th>
                      <th>Created</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {row.name} <span className="muted mono">{row.slug}</span>
                        </td>
                        <td>
                          <span className={row.plan === 'paid' ? 'pill paid' : 'pill'}>
                            {row.plan}
                          </span>
                        </td>
                        <td className="num">{row.playgroundSeats}</td>
                        {/* The default is not called out as one: a team on `medium` is every team,
                            and a badge saying so on nearly every row is noise. */}
                        <td className="muted">{row.sandboxSize}</td>
                        <td className="num">{row.members}</td>
                        <td className="num">{row.games}</td>
                        <td className="muted">
                          <Time at={row.createdAt} />
                        </td>
                        <td>
                          <button onClick={() => setSelected(row.id)}>Open</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Async>
        <ListMore state={list} shown={shown} noun="teams" onMore={showMore} />
      </Panel>

      {selected ? <Team teamId={selected} onClose={() => setSelected(null)} /> : null}
    </>
  );
}

/**
 * One team, in a modal.
 *
 * A modal rather than a panel below the list, because this screen is a *lookup* followed by an
 * edit: the list is how a team is found, the detail is what is done to it, and pushing the list
 * three screens down the page every time one row is opened loses the place somebody worked to get
 * to. The fragment still addresses it (`#teams/<id>`), so the URL is as pasteable as it was.
 */
function Team({ teamId, onClose }: { teamId: string; onClose: () => void }) {
  const detail = useAsync(() => api<AdminTeamDetail>(`/v1/admin/teams/${teamId}`), [teamId]);
  const [seats, setSeats] = useState<string>('');
  const [minutes, setMinutes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  async function saveSeats() {
    setError(null);
    try {
      await put(`/v1/admin/teams/${teamId}/seats`, { seats: Number(seats) });
      setSeats('');
      detail.reload();
    } catch (failure) {
      setError(String(failure));
    }
  }

  async function saveSize(size: string) {
    setError(null);
    try {
      await put(`/v1/admin/teams/${teamId}/sandbox-size`, { size });
      detail.reload();
    } catch (failure) {
      setError(String(failure));
    }
  }

  async function saveClaudeCodeSize(size: string) {
    setError(null);
    try {
      await put(`/v1/admin/teams/${teamId}/claudecode-size`, { size });
      detail.reload();
    } catch (failure) {
      setError(String(failure));
    }
  }

  async function saveMinutes() {
    setError(null);
    try {
      await put(`/v1/admin/teams/${teamId}/sandbox-minutes`, { minutes: Number(minutes) });
      setMinutes('');
      detail.reload();
    } catch (failure) {
      setError(String(failure));
    }
  }

  return (
    // Titled before the read lands, so the modal does not open blank and then re-label itself.
    <Modal
      title={detail.data?.team.name ?? 'Team'}
      onClose={onClose}
      actions={
        detail.data ? (
          <>
            <a className="btn" href={`#games?team=${teamId}`}>Games</a>
            <label className="filter-inline">
              <span className="muted small">Plan</span>
              <select
                value={detail.data.team.plan}
                onChange={(e) => {
                  const plan = e.target.value;
                  // Every member's paid features move with it, so it is asked rather than assumed.
                  if (!window.confirm(`Move ${detail.data!.team.name} to the ${plan} plan? Every member's paid features change with it.`)) return;
                  void put(`/v1/admin/teams/${teamId}/plan`, { plan }).then(detail.reload);
                }}
              >
                <option value="free">free</option>
                <option value="paid">paid</option>
              </select>
            </label>
          </>
        ) : null
      }
    >
      <Async state={detail}>
        {(data) => (
          <>
            <Panel title="Sandboxes">
              <div className="row">
                <label htmlFor="seats">Sandbox seats</label>
                <input
                  id="seats"
                  type="number"
                  min={0}
                  max={50}
                  style={{ width: 80 }}
                  value={seats === '' ? data.team.playgroundSeats : seats}
                  onChange={(e) => setSeats(e.target.value)}
                />
                <button onClick={() => void saveSeats()} disabled={seats === ''}>
                  Save
                </button>
                {/* Migration 0017 bounds the column at 0–50; the route checks the same range so a
                    bad number is a message rather than a 500. */}
                <span className="muted small">Playgrounds at once, 0–50.</span>
              </div>

              <div className="row">
                <label htmlFor="size">Container size</label>
                {/* Saved on change rather than behind a Save button, unlike the seats beside it:
                    this is a choice from a list, so there is no half-typed state a stray blur could
                    commit. The options come from `SANDBOX_SIZES` in the shared package — the same
                    list the template builder reads — so the machine each word means is one
                    hand-written agreement rather than two. */}
                <select
                  id="size"
                  value={data.team.sandboxSize}
                  onChange={(e) => void saveSize(e.target.value)}
                >
                  {SANDBOX_SIZES.map((spec) => (
                    <option key={spec.size} value={spec.size}>
                      {spec.label}
                      {spec.size === DEFAULT_SANDBOX_SIZE ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                <span
                  className="muted small"
                  title="It does not resize one somebody is working in. A size this deployment has published no template for fails the next start with a message naming the missing var."
                >
                  From the next sandbox.
                </span>
              </div>

              <div className="row">
                <label htmlFor="claudecode-size">Claude Code container size</label>
                {/* Only the sizes the Claude Code image is published at — the ones with the memory
                    Claude Code asks for — so there is nothing smaller to pick. */}
                <select
                  id="claudecode-size"
                  value={data.team.claudeCodeSandboxSize}
                  onChange={(e) => void saveClaudeCodeSize(e.target.value)}
                >
                  {CLAUDECODE_SANDBOX_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {sandboxSizeSpec(size).label}
                      {size === DEFAULT_CLAUDECODE_SANDBOX_SIZE ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                <span className="muted small">Claude Code sandboxes, from the next one.</span>
              </div>

              <div className="row">
                <label htmlFor="minutes">Session length</label>
                <input
                  id="minutes"
                  type="number"
                  min={MIN_SANDBOX_MINUTES}
                  max={MAX_SANDBOX_MINUTES}
                  style={{ width: 80 }}
                  value={minutes === '' ? data.team.sandboxMinutes : minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                />
                <button onClick={() => void saveMinutes()} disabled={minutes === ''}>
                  Save
                </button>
                {/* Migration 0047 bounds the column; the route checks the same range. */}
                <span
                  className="muted small"
                  title="How long one sandbox lasts before it hands over to a fresh one, and for Claude Code before the developer signs in again. E2B's Hobby plan ends every sandbox at 60 whatever this says."
                >
                  Minutes, {MIN_SANDBOX_MINUTES}–{MAX_SANDBOX_MINUTES} ({DEFAULT_SANDBOX_MINUTES} by default), from the next sandbox.
                </span>
              </div>
              {error ? <p className="error">{error}</p> : null}
            </Panel>

            {/* Boardaux's own teams (migration 0097): the only ones whose games can have printed
                figures. On does not switch any game on; that is the Figures tab's, per game. */}
            <Panel title="Printed figures">
              <div className="row">
                <label className="filter-inline">
                  <input
                    type="checkbox"
                    checked={data.team.firstParty}
                    onChange={(e) => {
                      const on = e.target.checked;
                      if (!on && !window.confirm(`Stop ${data.team.name}'s games making and selling printed figures? Work already running finishes.`)) return;
                      setError(null);
                      void put(`/v1/admin/teams/${teamId}/first-party`, { on }).then(detail.reload, (failure) => setError(String(failure)));
                    }}
                  />
                  Boardaux's own team
                </label>
                <span className="muted small">
                  {data.team.firstParty ? (
                    <>
                      Its games with a playground can have figures. Switch them on per game in <a href="#figures">Figures</a>.
                    </>
                  ) : (
                    'Only Boardaux’s own teams can make and sell printed figures.'
                  )}
                </span>
              </div>
            </Panel>

            <Panel title="Members">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Handle</th>
                      <th>Role</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.members.map((member) => (
                      <tr key={member.userId}>
                        {/* Handles only. An admin is not a member of this team (§D7); the reveal
                            on the Accounts screen is how a name comes out, one person at a time. */}
                        <td className="mono"><a href={`#accounts/${member.userId}`}>@{member.username}</a></td>
                        <td className="muted">{member.role}</td>
                        <td className="muted">
                          <Time at={member.joinedAt} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel title="Features for this team">
              {/* From the plan alone. A beta or alpha feature is each member's own tier, and reads
                  "per person" here rather than a guess; a member's page lists what they have in
                  this team. */}
              <FeatureList features={data.features} other="per person" />
            </Panel>
          </>
        )}
      </Async>
    </Modal>
  );
}
